import { useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, type InitUploadDocumentDto, extractErrorMessage } from '@sib/api';
import { QueryKey } from '@sib/api/enums';
import { useUserSetupStore } from '@sib/auth';
import { DOCUMENTS_DROPZONE_MAX_SIZE_BYTES } from '@sib/config';
import { useTranslation } from '@sib/i18n';
import { useNotification } from '@sib/notifications';
import { FileItem } from '@sib/types';
import { v4 as uuidv4 } from 'uuid';
import {
  isInvalidBlobContentError,
  uploadFileInChunks,
} from './chunked-upload';
import {
  buildChunkedUploadSessionKey,
  clearPersistedChunkedUploadSession,
  loadPersistedChunkedUploadSession,
  markPersistedChunkedUploadBlockComplete,
  pruneStaleUploadSessions,
  resetPersistedChunkedUploadBlockProgress,
  savePersistedChunkedUploadSession,
} from './chunked-upload-session';
import {
  DocumentUploadFileWithMd5,
  validateDocumentUploadByMd5,
} from './document-upload-md5';
import { FileUploadWithCallbacksProps } from './types';
import { isAbortError } from './utils';

interface UploadToBlobStorageProps extends FileUploadWithCallbacksProps {}

type UploadDocumentFileOptions = {
  batchUploadId?: string;
  totalFiles: number;
  md5Hash: string;
  abortSignal?: AbortSignal;
};

type ValidateDocumentUploadOptions = {
  fileItems: FileItem[];
  resolveFileItem?: (fileId: string) => FileItem | undefined;
};

const CHUNKED_UPLOAD_SESSION_TTL_MS = 24 * 60 * 60 * 1000;

export const useUploadToBlobStorage = ({
  onProgress,
  onError,
  onSuccess,
}: UploadToBlobStorageProps) => {
  const { t } = useTranslation();
  const { open } = useNotification();
  const queryClient = useQueryClient();
  const {
    organization: { id: organizationId },
  } = useUserSetupStore();

  useEffect(() => {
    pruneStaleUploadSessions(CHUNKED_UPLOAD_SESSION_TTL_MS);
  }, []);

  const validateDocumentUpload = useCallback(
    async ({
      fileItems,
      resolveFileItem,
    }: ValidateDocumentUploadOptions): Promise<DocumentUploadFileWithMd5[]> => {
      try {
        const duplicateErrors: string[] = [];

        const validatedFiles = await validateDocumentUploadByMd5({
          organizationId: organizationId || '',
          fileItems,
          resolveFileItem,
          onDuplicate: (fileItem, error) => {
            duplicateErrors.push(error);
            onError?.(fileItem, error);
          },
        });

        if (duplicateErrors.length > 0) {
          const errorDescription = [
            ...new Set(duplicateErrors.filter(Boolean)),
          ].join('\n');

          if (errorDescription) {
            open?.({
              type: 'error',
              message: t('fileManager.uploadFailedMessage'),
              description: errorDescription,
            });
          }
        }

        return validatedFiles;
      } catch (error) {
        const message = extractErrorMessage(
          error,
          t('fileManager.uploadFailedMessage'),
          { defaultMessageStatusCodes: [500, 404] }
        );

        fileItems.forEach(fileItem => onError?.(fileItem, message, error));

        return [];
      }
    },
    [onError, open, organizationId, t]
  );

  const uploadDocumentFile = useCallback(
    async (fileItem: FileItem, options: UploadDocumentFileOptions) => {
      const { batchUploadId, totalFiles, md5Hash, abortSignal } = options;

      onProgress?.(fileItem, 0);

      const initPayload: InitUploadDocumentDto = {
        filename: fileItem.file.name,
        mimetype: fileItem.file.type || 'application/octet-stream',
        totalFiles,
        batchUploadId,
        sensitive: fileItem.isSensitive ?? false,
        md5Hash,
        filesize: fileItem.file.size,
      };

      const sessionKey = buildChunkedUploadSessionKey({
        organizationId: organizationId || '',
        filename: fileItem.file.name,
        filesize: fileItem.file.size,
        md5Hash,
      });

      const runUploadAttempt = async (restart: boolean) => {
        const session =
          await api.organization.documentControllerInitUploadSession(
            organizationId || '',
            { ...initPayload, restart: restart || undefined },
            { signal: abortSignal }
          );

        const completedBlockIndices = new Set<number>();
        const canResumePartialBlocks =
          !restart &&
          session.resumePartialBlocks &&
          loadPersistedChunkedUploadSession(sessionKey)?.uploadId ===
            session.uploadId;

        if (canResumePartialBlocks) {
          const persistedSession =
            loadPersistedChunkedUploadSession(sessionKey);

          persistedSession?.completedBlockIndices.forEach(blockIndex => {
            completedBlockIndices.add(blockIndex);
          });
        } else if (restart) {
          clearPersistedChunkedUploadSession(sessionKey);
          onProgress?.(fileItem, 0);
        } else if (
          loadPersistedChunkedUploadSession(sessionKey)?.uploadId ===
          session.uploadId
        ) {
          resetPersistedChunkedUploadBlockProgress(sessionKey);
        }

        savePersistedChunkedUploadSession(sessionKey, {
          organizationId: organizationId || '',
          filename: fileItem.file.name,
          filesize: fileItem.file.size,
          md5Hash,
          uploadId: session.uploadId,
          blockSize: session.blockSize,
          completedBlockIndices: [...completedBlockIndices],
          createdAt: Date.now(),
        });

        const { blockIds } = await uploadFileInChunks({
          file: fileItem.file,
          sasUrl: session.sasUrl,
          blockSize: session.blockSize,
          md5HashBase64: md5Hash,
          abortSignal,
          completedBlockIndices,
          onBlockComplete: blockIndex => {
            markPersistedChunkedUploadBlockComplete(sessionKey, blockIndex);
          },
          onProgress: (loaded, total) => {
            onProgress?.(fileItem, Math.round((loaded * 100) / total));
          },
        });

        await api.organization.documentControllerCompleteUploadSession(
          organizationId || '',
          {
            uploadId: session.uploadId,
            blockIds,
          },
          { signal: abortSignal }
        );

        clearPersistedChunkedUploadSession(sessionKey);
      };

      try {
        if (fileItem.file.size > DOCUMENTS_DROPZONE_MAX_SIZE_BYTES) {
          onError?.(
            fileItem,
            t('fileManager.maxFileSize', {
              size: `${DOCUMENTS_DROPZONE_MAX_SIZE_BYTES / (1024 * 1024 * 1024)} GB`,
            })
          );

          return;
        }

        try {
          await runUploadAttempt(false);
        } catch (error) {
          if (isAbortError(error)) {
            clearPersistedChunkedUploadSession(sessionKey);
            onError?.(fileItem, t('fileManager.uploadCancelledMessage'), error);

            return;
          }

          if (isInvalidBlobContentError(error)) {
            await runUploadAttempt(true);
          } else {
            clearPersistedChunkedUploadSession(sessionKey);
            throw error;
          }
        }

        await queryClient.invalidateQueries({
          queryKey: [QueryKey.Resource.Documents],
        });

        onProgress?.(fileItem, 100);
        onSuccess?.(fileItem);
      } catch (error) {
        if (isAbortError(error)) {
          clearPersistedChunkedUploadSession(sessionKey);
          onError?.(fileItem, t('fileManager.uploadCancelledMessage'), error);

          return;
        }

        clearPersistedChunkedUploadSession(sessionKey);
        onError?.(
          fileItem,
          extractErrorMessage(error, t('fileManager.uploadFailedMessage')),
          error
        );
      }
    },
    [onError, onProgress, onSuccess, organizationId, queryClient, t]
  );

  return {
    validateDocumentUpload,
    uploadDocumentFile,
  };
};

export const useBatchUpload = () => {
  const generateBatchId = useCallback(() => uuidv4(), []);

  return {
    generateBatchId,
  };
};
